import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, Link as LinkIcon, Copy, Trash2, ExternalLink, Loader2, ArrowLeft } from 'lucide-react';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';
import { CreatePaymentLinkPane } from '@/components/business/payments/CreatePaymentLinkPane';
import { PaymentLinkDetailsPane } from '@/components/business/payments/PaymentLinkDetailsPane';

interface LineItem {
  description: string;
  amount: number;
  quantity: number;
}

interface PaymentLink {
  id: string;
  token: string;
  title: string;
  notes?: string;
  total_amount: number;
  status: string;
  expires_at?: string;
  created_at: string;
  line_items: LineItem[];
  shareUrl: string;
}

export const PaymentLinksPage: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [links, setLinks] = useState<PaymentLink[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Sidebar state
  const [showCreate, setShowCreate] = useState(false);
  const [selectedToken, setSelectedToken] = useState<string | null>(null);
  const [sidebarWidth, setSidebarWidth] = useState(400);
  const isDragging = useRef(false);

  const fetchLinks = async () => {
    try {
      const { data } = await apiClient.get('/payment-links', { params: { businessId } });
      setLinks(data.links || []);
    } catch (e: any) {
      toast.error('Error', e.response?.data?.error || 'Failed to load payment links');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLinks();
  }, [businessId]);

  // Resizable sidebar logic
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      const newWidth = document.body.clientWidth - e.clientX;
      setSidebarWidth(Math.max(300, Math.min(newWidth, 800)));
    };
    const handleMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = 'default';
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    document.body.style.cursor = 'col-resize';
  };

  const handleCancel = async (token: string) => {
    if (!window.confirm('Are you sure you want to cancel this payment link?')) return;
    try {
      await apiClient.delete(`/payment-links/${token}`, { params: { businessId } });
      toast.success('Cancelled', 'Payment link has been cancelled');
      fetchLinks();
    } catch (e: any) {
      toast.error('Error', e.response?.data?.error || 'Failed to cancel link');
    }
  };

  const copyToClipboard = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success('Copied', 'Payment link copied to clipboard');
  };

  return (
    <div className="h-screen flex flex-col bg-light-bg dark:bg-dark-bg overflow-hidden">
      {/* ── Header ── */}
      <div className="flex-shrink-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border">
        <div className="w-full flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(`/business/${businessId}`)} className="p-2 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors">
              <ArrowLeft size={24} style={{ color: '#4F46E5' }} />
            </button>
            <div>
              <p className="font-bold text-lg" style={{ color: '#4F46E5' }}>Payment Links</p>
              <p className="text-xs" style={{ color: '#64748B' }}>
                Securely collect money from customers anywhere
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              setSelectedToken(null);
              setShowCreate(true);
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary hover:bg-primary/90 transition-colors shadow-sm"
          >
            <Plus size={20} className="text-white" />
            <span className="font-semibold text-sm text-white">Create Link</span>
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* ── Main List Column ── */}
        <div className="flex-1 flex flex-col overflow-hidden relative">
          <div className="flex-1 overflow-y-auto px-4 pt-6 pb-24 scrollbar-thin">
            {loading ? (
              <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
            ) : links.length === 0 ? (
              <div className="text-center py-16 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-2xl max-w-lg mx-auto shadow-sm">
                <div className="w-16 h-16 bg-light-bg dark:bg-dark-bg rounded-full flex items-center justify-center mx-auto mb-4">
                  <LinkIcon className="w-8 h-8 text-light-subtle dark:text-dark-subtle" />
                </div>
                <h3 className="text-lg font-bold text-light-text dark:text-dark-text mb-2">No Payment Links</h3>
                <p className="text-light-subtle dark:text-dark-subtle max-w-sm mx-auto mb-6">Create your first payment link to securely collect money from customers anywhere.</p>
                <button onClick={() => setShowCreate(true)} className="bg-primary text-white px-6 py-2.5 rounded-xl font-bold hover:bg-primary/90 transition shadow-sm">
                  Create Link
                </button>
              </div>
            ) : (
              <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {links.map(link => (
                  <div 
                    key={link.id} 
                    onClick={() => {
                      setShowCreate(false);
                      setSelectedToken(link.token);
                    }}
                    className={`bg-light-card dark:bg-dark-card border rounded-2xl p-5 shadow-sm hover:shadow-md transition-all flex flex-col cursor-pointer ${
                      selectedToken === link.token 
                        ? 'border-primary ring-1 ring-primary' 
                        : 'border-light-border dark:border-dark-border'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <h4 className="font-bold text-light-text dark:text-dark-text truncate pr-2">{link.title}</h4>
                      <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${
                        link.status === 'paid' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' :
                        link.status === 'cancelled' ? 'bg-red-500/10 text-red-600 dark:text-red-400' :
                        link.status === 'expired' ? 'bg-gray-500/10 text-gray-600 dark:text-gray-400' :
                        'bg-primary/10 text-primary'
                      }`}>
                        {link.status}
                      </span>
                    </div>
                    <div className="text-2xl font-black text-light-text dark:text-dark-text mb-4">
                      KSH {link.total_amount.toLocaleString()}
                    </div>
                    <div className="mt-auto pt-4 border-t border-light-border dark:border-dark-border flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(link.shareUrl);
                        }}
                        className="flex-1 flex items-center justify-center gap-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border py-2 rounded-lg text-sm font-bold text-light-text dark:text-dark-text hover:bg-light-border dark:hover:border-dark-border transition"
                      >
                        <Copy size={16} /> Copy URL
                      </button>
                      {link.status === 'active' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCancel(link.token);
                          }}
                          className="p-2 text-red-500 border border-light-border dark:border-dark-border rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 transition"
                          title="Cancel Link"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                      <a
                        href={link.shareUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="p-2 text-light-subtle dark:text-dark-subtle border border-light-border dark:border-dark-border rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition"
                        title="Open Link"
                      >
                        <ExternalLink size={16} />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Resizable Sidebar ── */}
        {(showCreate || selectedToken) && (
          <>
            <div
              className="w-1 cursor-col-resize hover:bg-primary/50 active:bg-primary bg-light-border dark:bg-dark-border transition-colors flex-shrink-0 z-50"
              onMouseDown={handleMouseDown}
            />
            <div style={{ width: sidebarWidth }} className="flex-shrink-0 bg-light-card dark:bg-dark-card shadow-[-4px_0_15px_rgba(0,0,0,0.05)] z-40 relative">
              {showCreate ? (
                <CreatePaymentLinkPane
                  businessId={businessId!}
                  onCancel={() => setShowCreate(false)}
                  onSuccess={() => {
                    setShowCreate(false);
                    fetchLinks();
                  }}
                />
              ) : selectedToken ? (
                <PaymentLinkDetailsPane
                  businessId={businessId!}
                  token={selectedToken}
                  onClose={() => setSelectedToken(null)}
                />
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentLinksPage;
